import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiSearch, FiDownload, FiShield, FiRefreshCw } from "react-icons/fi";
import Swal from "sweetalert2";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminAuditLogs.css";

const API = getApiBaseUrl();

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/audit-logs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: {
          search: search || undefined,
          action_type: actionFilter !== "all" ? actionFilter : undefined,
          limit: 200,
        },
      });
      setLogs(res.data || []);
    } catch (err) {
      console.error("Failed to load audit logs:", err);
      Swal.fire("Error", "Failed to fetch audit logs", "error");
    } finally {
      setLoading(false);
    }
  }, [search, actionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Export to CSV
  const exportToCSV = () => {
    if (logs.length === 0) {
      Swal.fire("Info", "No logs available to export", "info");
      return;
    }

    const headers = ["ID", "Admin Email", "Action Type", "Target Entity", "Details", "IP Address", "Date & Time"];
    const rows = logs.map((log) => [
      log.id,
      `"${log.admin_email || ""}"`,
      `"${log.action_type || ""}"`,
      `"${log.target_entity || ""}"`,
      `"${(log.details || "").replace(/"/g, '""')}"`,
      `"${log.ip_address || ""}"`,
      `"${new Date(log.created_at).toLocaleString()}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `soloway_audit_logs_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getActionBadgeClass = (action) => {
    if (action.includes("PAYOUT") || action.includes("FINANCE")) return "badge-payout";
    if (action.includes("COMMISSION") || action.includes("LIMIT")) return "badge-commission";
    if (action.includes("REFUND")) return "badge-refund";
    if (action.includes("BROADCAST")) return "badge-broadcast";
    if (action.includes("USER") || action.includes("STATUS")) return "badge-user";
    return "badge-default";
  };

  return (
    <div className="aal-container">
      <div className="aal-header">
        <div>
          <h2 className="aal-title">
            <FiShield className="aal-title-icon" /> Security & Admin Audit Trail
          </h2>
          <p className="aal-subtitle">
            Immutable log of all administrative actions, financial approvals, rate modifications, and system events.
          </p>
        </div>
        <div className="aal-actions">
          <button className="aal-btn-secondary" onClick={fetchLogs} title="Refresh logs">
            <FiRefreshCw /> Refresh
          </button>
          <button className="aal-btn-primary" onClick={exportToCSV} title="Export CSV for compliance">
            <FiDownload /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="aal-toolbar">
        <div className="aal-search-box">
          <FiSearch className="aal-search-icon" />
          <input
            type="text"
            placeholder="Search by admin email, entity, or details..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="aal-filter-box">
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="all">All Action Types</option>
            <option value="PAYOUT_PROCESSED">Payout Disbursals</option>
            <option value="COMMISSION_UPDATED">Commission Updates</option>
            <option value="REFUND_STATUS_UPDATED">Refunds & Disputes</option>
            <option value="BROADCAST_SENT">Broadcast Notifications</option>
            <option value="USER_STATUS_CHANGED">User Account Moderation</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="aal-table-wrapper">
        {loading ? (
          <div className="aal-loading">Loading audit records...</div>
        ) : logs.length === 0 ? (
          <div className="aal-empty">No audit records found matching your filters.</div>
        ) : (
          <table className="aal-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Timestamp</th>
                <th>Admin Email</th>
                <th>Action Type</th>
                <th>Target Entity</th>
                <th>Details & Payload</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td><strong>#{log.id}</strong></td>
                  <td className="aal-time">{new Date(log.created_at).toLocaleString()}</td>
                  <td>{log.admin_email || "System Super Admin"}</td>
                  <td>
                    <span className={`aal-badge ${getActionBadgeClass(log.action_type)}`}>
                      {log.action_type}
                    </span>
                  </td>
                  <td><strong>{log.target_entity || "System"}</strong></td>
                  <td className="aal-details" title={log.details}>
                    {log.details ? (log.details.length > 80 ? `${log.details.slice(0, 80)}...` : log.details) : "—"}
                  </td>
                  <td className="aal-ip">{log.ip_address || "127.0.0.1"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
